import i18nFactory from '@yandex-int/i18n';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { selectMetrikaGoals } from '../../selectors/campaignsEdit/campaignsEdit';
import { ValidationErrorData } from '../../typings/common';
import { CurrencyLimitData } from '../../typings/constants';
import { Goal } from '../../typings/goals';
import { CurrencyCode } from '../../typings/graphql-grid';
import { State } from '../../typings/state';
import {
    DnaStrategyType,
    OptimizationCriteria,
    StrategyEditorValidationResult,
    StrategyErrorType,
    StrategyForEditor,
    StrategyForEditorManual,
    StrategyForEditorOptimizeClicks,
    StrategyForEditorOptimizeCpa,
    StrategyForEditorRoi
} from '../../typings/strategy';
import {
    isGoalDeleted,
    StrategyWithGoalId,
    StrategyWithMaxClickPrice,
    StrategyWithWeeklyBudget
} from '../StrategyEditorHelpers/StrategyEditorHelpers';

import * as keyset from './StrategyEditorValidators.i18n';

const i18n = i18nFactory(keyset);

export const validateWeeklyBudget = (
    strategy: StrategyWithWeeklyBudget,
    constants: CurrencyLimitData,
    currency: string
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];
    const { weeklyBudget } = strategy;
    let weeklyBudgetMandatory = false;

    if (strategy.type === DnaStrategyType.OPTIMIZE_CLICKS) {
        weeklyBudgetMandatory =
            strategy.optimizationCriteria ===
                OptimizationCriteria.WEEKLY_BUDGET ||
            (strategy.optimizationCriteria === OptimizationCriteria.AVG_CLICK &&
                strategy.weeklyBudgetEnabled);
    }

    if (!weeklyBudget.numVal) {
        if (weeklyBudgetMandatory) {
            errors.push({
                code: 'WEEKLY_BUDGET_MANDATORY',
                text: i18n('weekly-budget-mandatory')
            });
        }
        return errors;
    }

    if (weeklyBudget.numVal < constants.minAutobudget) {
        errors.push({
            code: 'SMALL_WEEK_BUDGET',
            text: i18n('too-small-weekly-budget', {
                limit: constants.minAutobudget,
                currency
            })
        });
    }
    if (weeklyBudget.numVal > constants.maxAutobudget) {
        errors.push({
            code: 'BIG_WEEK_BUDGET',
            text: i18n('too-big-weekly-budget', {
                limit: constants.maxAutobudget,
                currency
            })
        });
    }
    return errors;
};

export const validateDailyBudget = (
    { dailyBudget }: StrategyForEditorManual,
    constants: CurrencyLimitData,
    currency: string
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];

    if (!dailyBudget.numVal) {
        return errors;
    }
    if (dailyBudget.numVal < constants.minDailyBudget) {
        errors.push({
            code: 'SMALL_DAY_BUDGET',
            text: i18n('too-small-daily-budget', {
                limit: constants.minDailyBudget,
                currency
            })
        });
    }
    if (dailyBudget.numVal > constants.maxDailyBudget) {
        errors.push({
            code: 'BIG_DAY_BUDGET',
            text: i18n('too-big-daily-budget', {
                limit: constants.maxDailyBudget,
                currency
            })
        });
    }
    return errors;
};

export const validateMaxClickPrice = (
    strategy: StrategyWithMaxClickPrice,
    constants: CurrencyLimitData,
    currency: string
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];
    const { maxClickPrice } = strategy;
    const maxClickPriceDisabled =
        strategy.type === DnaStrategyType.OPTIMIZE_CLICKS &&
        (strategy.optimizationCriteria === OptimizationCriteria.AVG_CLICK ||
            (strategy.optimizationCriteria ===
                OptimizationCriteria.WEEK_BUNDLE &&
                strategy.avgClickPriceEnabled));

    if (!maxClickPrice.numVal || maxClickPriceDisabled) {
        return errors;
    }
    if (maxClickPrice.numVal < constants.minAutobudgetBid) {
        errors.push({
            code: 'SMALL_MAX_PRICE',
            text: i18n('too-small-max-click-price', {
                limit: constants.minAutobudgetBid,
                currency
            })
        });
    }
    if (maxClickPrice.numVal > constants.maxAutobudgetBid) {
        errors.push({
            code: 'BIG_MAX_PRICE',
            text: i18n('too-big-max-click-price', {
                limit: constants.maxAutobudgetBid,
                currency
            })
        });
    }
    return errors;
};

export const validateAvgClickPrice = (
    {
        avgClickPrice,
        optimizationCriteria,
        avgClickPriceEnabled
    }: StrategyForEditorOptimizeClicks,
    constants: CurrencyLimitData,
    currency: string
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];
    const avgClickPriceMandatory =
        optimizationCriteria === OptimizationCriteria.AVG_CLICK ||
        (optimizationCriteria === OptimizationCriteria.WEEK_BUNDLE &&
            avgClickPriceEnabled);

    if (!avgClickPrice.numVal) {
        if (avgClickPriceMandatory) {
            errors.push({
                code: 'AVG_CLICK_PRICE_MANDATORY',
                text: i18n('avg-click-price-mandatory')
            });
        }
        return errors;
    }
    if (avgClickPrice.numVal < constants.minAutobudgetAvgPrice) {
        errors.push({
            code: 'SMALL_CLICK_PRICE',
            text: i18n('too-small-avg-click-price', {
                limit: constants.minAutobudgetAvgPrice,
                currency
            })
        });
    }
    if (avgClickPrice.numVal > constants.maxAutobudgetBid) {
        errors.push({
            code: 'BIG_CLICK_PRICE',
            text: i18n('too-big-avg-click-price', {
                limit: constants.maxAutobudgetBid,
                currency
            })
        });
    }
    return errors;
};

export const validateWeekBundle = (
    { weekBundle, optimizationCriteria }: StrategyForEditorOptimizeClicks,
    constants: CurrencyLimitData
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];

    if (optimizationCriteria !== OptimizationCriteria.WEEK_BUNDLE) {
        return errors;
    }

    if (!weekBundle.numVal) {
        errors.push({
            code: 'WEEK_BUNDLE_MANDATORY',
            text: i18n('week-bundle-mandatory')
        });
        return errors;
    }

    if (weekBundle.numVal < constants.minAutobudgetClicksBundle) {
        errors.push({
            code: 'SMALL_WEEK_BUNDLE',
            text: i18n('too-small-week-bundle', {
                limit: constants.minAutobudgetClicksBundle
            })
        });
    }

    if (weekBundle.numVal > constants.maxAutobudgetClicksBundle) {
        errors.push({
            code: 'BIG_WEEK_BUNDLE',
            text: i18n('too-big-week-bundle', {
                limit: constants.maxAutobudgetClicksBundle
            })
        });
    }

    return errors;
};

export const validateAvgCpaPrice = (
    { avgCpaPrice }: StrategyForEditorOptimizeCpa,
    constants: CurrencyLimitData,
    currency: string
): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];

    if (!avgCpaPrice.numVal) {
        return errors;
    }
    if (avgCpaPrice.numVal < constants.minAutobudgetAvgCpa) {
        errors.push({
            code: 'SMALL_CPA_PRICE',
            text: i18n('too-small-avg-cpa-price', {
                limit: constants.minAutobudgetAvgCpa,
                currency
            })
        });
    }
    if (avgCpaPrice.numVal > constants.autobudgetAvgCpaWarning) {
        errors.push({
            code: 'BIG_CPA_PRICE',
            text: i18n('too-big-avg-cpa-price', {
                limit: constants.autobudgetAvgCpaWarning,
                currency
            })
        });
    }
    return errors;
};

const FROM_PERCENT = 0;
const TO_PERCENT = 100;

export const validateProfitability = ({
    profitability
}: StrategyForEditorRoi): ValidationErrorData[] => {
    const errors: ValidationErrorData[] = [];

    if (!profitability.numVal) {
        return errors;
    }
    if (
        profitability.numVal < FROM_PERCENT ||
        profitability.numVal > TO_PERCENT
    ) {
        errors.push({
            code: 'INVALID_PROFITABILITY',
            text: i18n('invalid-profitability', {
                from: FROM_PERCENT,
                to: TO_PERCENT
            })
        });
    }
    return errors;
};

export const validateRoiCoef = ({
    roiCoef
}: StrategyForEditorRoi): ValidationErrorData[] => {
    if (!roiCoef.numVal) {
        return [
            {
                code: 'INVALID_PROFITABILITY',
                text: i18n('roi-coef-mandatory')
            }
        ];
    }
    return [];
};

const validateGoalId = (
    { goalId }: StrategyWithGoalId,
    metrikaGoals: Record<string, Goal>
): ValidationErrorData[] => {
    if (isGoalDeleted(metrikaGoals, goalId)) {
        return [
            {
                code: 'GOAL_DELETED',
                text: ''
            }
        ];
    }
    return [];
};

export const validateManualStrategy = (
    strategy: StrategyForEditorManual,
    constants: CurrencyLimitData,
    currency: string
): StrategyEditorValidationResult => {
    const errors: StrategyEditorValidationResult = {};

    errors.dailyBudget = validateDailyBudget(strategy, constants, currency);
    // здесь нужно добавить проверку количества изменений дневого бюджета
    // https://st.yandex-team.ru/DIRECT-102613

    return errors;
};

export const validateRoiStrategy = (
    strategy: StrategyForEditorRoi,
    constants: CurrencyLimitData,
    currency: string,
    metrikaGoals: Record<string, Goal>
): StrategyEditorValidationResult => {
    const errors: StrategyEditorValidationResult = {};

    errors.weeklyBudget = validateWeeklyBudget(strategy, constants, currency);
    errors.maxClickPrice = validateMaxClickPrice(strategy, constants, currency);
    errors.profitability = validateProfitability(strategy);
    errors.roiCoef = validateRoiCoef(strategy);
    errors.goalId = validateGoalId(strategy, metrikaGoals);

    return errors;
};

export const validateOptimizeClicksStrategy = (
    strategy: StrategyForEditorOptimizeClicks,
    constants: CurrencyLimitData,
    currency: string
): StrategyEditorValidationResult => {
    const errors: StrategyEditorValidationResult = {};

    errors.weeklyBudget = validateWeeklyBudget(strategy, constants, currency);
    errors.maxClickPrice = validateMaxClickPrice(strategy, constants, currency);
    errors.avgClickPrice = validateAvgClickPrice(strategy, constants, currency);
    errors.weekBundle = validateWeekBundle(strategy, constants);

    return errors;
};

export const validateOptimizeCpaStrategy = (
    strategy: StrategyForEditorOptimizeCpa,
    constants: CurrencyLimitData,
    currency: string,
    metrikaGoals: Record<string, Goal>
): StrategyEditorValidationResult => {
    const errors: StrategyEditorValidationResult = {};

    errors.weeklyBudget = validateWeeklyBudget(strategy, constants, currency);
    errors.maxClickPrice = validateMaxClickPrice(strategy, constants, currency);
    errors.avgCpaPrice = validateAvgCpaPrice(strategy, constants, currency);
    errors.goalId = validateGoalId(strategy, metrikaGoals);

    if (!strategy.avgCpaPrice.numVal && !strategy.weeklyBudget.numVal) {
        errors.optimizationParams = [
            {
                text: i18n('specify-avg-cpa-or-weekly-budget'),
                code: 'INVALID_OPTIMIZE_CPA_STRATEGY'
            }
        ];
    }

    return errors;
};

export const validateStrategy = (
    currencyCode: CurrencyCode,
    metrikaGoals: Record<string, Goal>,
    strategy: StrategyForEditor,
    constants?: CurrencyLimitData
): StrategyEditorValidationResult => {
    if (!constants) {
        return {
            common: [
                {
                    text: 'premature validation',
                    code: 'PREMATURE_VALIDATION'
                }
            ]
        };
    }
    const currency = mapStringToCurrencySymbol(currencyCode);

    switch (strategy.type) {
        case DnaStrategyType.MANUAL:
            return validateManualStrategy(strategy, constants, currency);

        case DnaStrategyType.OPTIMIZE_CLICKS:
            return validateOptimizeClicksStrategy(
                strategy,
                constants,
                currency
            );

        case DnaStrategyType.OPTIMIZE_CPA:
            return validateOptimizeCpaStrategy(
                strategy,
                constants,
                currency,
                metrikaGoals
            );

        case DnaStrategyType.ROI:
            return validateRoiStrategy(
                strategy,
                constants,
                currency,
                metrikaGoals
            );

        default:
            return {
                common: [
                    {
                        text: 'invalid strategy type',
                        code: 'INVALID_STRATEGY'
                    }
                ]
            };
    }
};

export const validateCampaignStrategyEditor = (
    editorId: string,
    state: State
): StrategyEditorValidationResult => {
    const editor = state.strategyEditor.editors[editorId];
    const {
        commonData: {
            client: { workCurrency },
            constants
        }
    } = state;

    const strategy = editor.strategies[editor.strategyType];
    const currencyConsts =
        constants && constants.currencyConstants[workCurrency];

    if (!strategy) {
        // недостижимое состояние
        return {
            [StrategyErrorType.common]: [
                {
                    text: 'nonexistent-strategy'
                }
            ]
        };
    }

    const metrikaGoals = selectMetrikaGoals(state);

    return validateStrategy(
        workCurrency,
        metrikaGoals,
        strategy,
        currencyConsts
    );
};

export const isStrategyValidationResultPositive = (
    validationResult: StrategyEditorValidationResult
): boolean =>
    !Object.keys(validationResult).some(
        key => validationResult[key].length !== 0
    );
